import * as THREE from 'three';
import { HalfFloatType } from 'three';
import {
  EffectComposer, EffectPass, RenderPass,
  SelectiveBloomEffect, VignetteEffect,
  SMAAEffect, SMAAPreset,
} from 'postprocessing';
import { N8AOPostPass } from 'n8ao';
import { CARD_THICKNESS, updateFoilSheens, setTextureRenderer, getFoilMeshes, consumeFoilMeshesDirty } from './cardMesh';
import { addTween, updateTweens } from './animations';
import { getInputMode } from './controlSettings';
import { perf } from '../perfMonitor';
import { getEffectivePixelRatio, getGraphicsSettings, onGraphicsChange } from './graphicsSettings';
import { isPhoneLayout } from '../platform';
import { buildTableEnvironment } from './tableEnvironment';
import { generatePlaymatUiTexture, disposePlaymatUiTextures, playmatTexelBudget } from './uiOverlayGenerator';

const TABLE_WIDTH = 200;
const TABLE_HEIGHT = Math.round(200 / (10300 / 7200));
const TABLE_THICKNESS = 3;
const TABLE_CORNER_RADIUS = 4;

const _right = new THREE.Vector3();
const _forward = new THREE.Vector3();
const _raycastTarget = new THREE.Vector3();
let pollId = null;

function createRoundedBoxGeometry(width, height, depth, radius) {
  const shape = new THREE.Shape();
  const w = width / 2;
  const h = height / 2;
  const r = Math.min(radius, w, h);

  shape.moveTo(-w + r, -h);
  shape.lineTo(w - r, -h);
  shape.quadraticCurveTo(w, -h, w, -h + r);
  shape.lineTo(w, h - r);
  shape.quadraticCurveTo(w, h, w - r, h);
  shape.lineTo(-w + r, h);
  shape.quadraticCurveTo(-w, h, -w, h - r);
  shape.lineTo(-w, -h + r);
  shape.quadraticCurveTo(-w, -h, -w + r, -h);

  return new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false, steps: 1 });
}

export function createTableScene(canvas, battlemapUrl, backgroundUrl) {
  // Check WebGL support on a throwaway canvas. Testing on the real canvas
  // then calling loseContext() leaves it in a "context lost" state that
  // Three.js can't recover from. The renderer gets a null context and
  // crashes with "Cannot read properties of null (reading 'precision')".
  const probe = document.createElement('canvas');
  const testCtx = probe.getContext('webgl2') || probe.getContext('webgl');
  if (!testCtx) {
    throw new Error('WebGL is not available. Your GPU or browser configuration may not support it.');
  }
  const loseExt = testCtx.getExtension('WEBGL_lose_context');
  if (loseExt) loseExt.loseContext();

  // Disable built-in MSAA. SMAA runs in the postprocessing pipeline
  // instead (cheaper, applied after all effects).
  // Disable stencil/depth on the default framebuffer since the
  // EffectComposer manages its own render targets.
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false,
    stencil: false,
    depth: false,
    powerPreference: 'high-performance',
    // The Moments screenshot re-renders on demand via __valkCaptureRender
    // (below) in the same task as its pixel grab, so the framebuffer does
    // not need to be preserved between composites. preserveDrawingBuffer
    // would pin a permanent extra copy of the drawing buffer.
    preserveDrawingBuffer: false,
  });

  // Expose the canvas globally so the Moments capture helper can grab
  // pixels directly from it, plus a synchronous re-render hook: with an
  // unpreserved drawing buffer the canvas is only readable in the same
  // task as a composite, so capture calls the hook, then reads. Both are
  // nulled on disposal (see returned dispose() handler below).
  if (typeof window !== 'undefined') {
    window.__valkCaptureCanvas = canvas;
    window.__valkCaptureRender = () => { try { composer.render(); } catch { /* mid-teardown */ } };
  }
  renderer.setPixelRatio(getEffectivePixelRatio());
  renderer.setClearColor(0x0e0b08);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  // Tone mapping is handled by the postprocessing pipeline's
  renderer.toneMapping = THREE.NoToneMapping;

  setTextureRenderer(renderer);

  const scene = new THREE.Scene();

  // ── Procedural environment map ──────────────────────────────
  scene.environment = buildTableEnvironment(renderer);

  // Near plane at 1.0 (not 0.1). The camera never approaches surfaces
  // closer than a few units, and the tighter near/far ratio gives 10x
  // better depth buffer precision. Eliminates z-fighting flicker between
  // card front/back faces and foil sheen overlays when zoomed out.
  const camera = new THREE.PerspectiveCamera(50, canvas.clientWidth / canvas.clientHeight, 1.0, 1000);

  // Orbit camera state
  let orbitTarget = new THREE.Vector3(0, 0, 0);
  let orbitDistance = 160;
  let orbitPhi = 0.1; // vertical angle: 0 = top-down, PI/2 = horizon
  let orbitTheta = 0; // horizontal rotation

  // Camera glide. Pan/zoom inputs write the *Goal state; the frame loop
  // eases the live values toward it with frame-rate-independent
  // exponential damping. Applying raw input 1:1 made panning read as
  // stutter: every wheel tick and mousemove quantum landed as a
  // discrete camera jump, and any frame hitch made the jump bigger.
  const orbitTargetGoal = new THREE.Vector3(0, 0, 0);
  let orbitDistanceGoal = 160;

  function updateCameraFromOrbit() {
    const x = orbitDistance * Math.sin(orbitPhi) * Math.sin(orbitTheta);
    const y = orbitDistance * Math.cos(orbitPhi);
    const z = orbitDistance * Math.sin(orbitPhi) * Math.cos(orbitTheta);
    camera.position.set(orbitTarget.x + x, orbitTarget.y + y, orbitTarget.z + z);
    camera.lookAt(orbitTarget);
  }

  updateCameraFromOrbit();

  const ambient = new THREE.AmbientLight(0xffffff, 0.6);
  scene.add(ambient);

  const directional = new THREE.DirectionalLight(0xffffff, 1.6);
  directional.position.set(40, 70, 25);
  directional.castShadow = true;
  directional.shadow.mapSize.width = 2048;
  directional.shadow.mapSize.height = 2048;
  directional.shadow.camera.left = -TABLE_WIDTH / 2;
  directional.shadow.camera.right = TABLE_WIDTH / 2;
  directional.shadow.camera.top = TABLE_WIDTH / 2;
  directional.shadow.camera.bottom = -TABLE_WIDTH / 2;
  directional.shadow.radius = 3;
  directional.shadow.bias = -0.0005;
  scene.add(directional);

  // ─── Time-of-day lighting ───
  // Night = full-moon silvery (still well-lit), day = warm golden.
  // Background art darkens at night instead of the table.
  // Night uses a blue/silver tint at FULL brightness. Games convey
  // nighttime through colour temperature, not darkness. Everything
  // stays readable and vivid; the blue palette just reads as "night."
  const TOD_KEYFRAMES = [
    //                    ambient RGB              aInt    directional RGB           dInt    bgDim   moonAngle
    { hour: 0,  ambCol: [0.72, 0.76, 0.92], ambInt: 0.65, dirCol: [0.78, 0.82, 0.95], dirInt: 1.4, bgDim: 0.60, moonAngle: 0.5 },
    { hour: 5,  ambCol: [0.72, 0.76, 0.92], ambInt: 0.65, dirCol: [0.78, 0.82, 0.95], dirInt: 1.4, bgDim: 0.60, moonAngle: 0.9 },
    { hour: 6,  ambCol: [0.95, 0.78, 0.58], ambInt: 0.60, dirCol: [1.00, 0.82, 0.58], dirInt: 1.4, bgDim: 0.70, moonAngle: 0.0 },
    { hour: 8,  ambCol: [1.00, 0.93, 0.84], ambInt: 0.65, dirCol: [1.00, 0.95, 0.87], dirInt: 1.5, bgDim: 0.85, moonAngle: 0.0 },
    { hour: 12, ambCol: [1.00, 0.95, 0.90], ambInt: 0.70, dirCol: [1.00, 0.96, 0.90], dirInt: 1.7, bgDim: 0.90, moonAngle: 0.0 },
    { hour: 17, ambCol: [1.00, 0.90, 0.78], ambInt: 0.65, dirCol: [1.00, 0.85, 0.65], dirInt: 1.5, bgDim: 0.85, moonAngle: 0.0 },
    { hour: 19, ambCol: [0.88, 0.68, 0.52], ambInt: 0.60, dirCol: [0.95, 0.70, 0.50], dirInt: 1.4, bgDim: 0.65, moonAngle: 0.0 },
    { hour: 21, ambCol: [0.75, 0.76, 0.90], ambInt: 0.65, dirCol: [0.80, 0.82, 0.95], dirInt: 1.4, bgDim: 0.60, moonAngle: 0.1 },
    { hour: 24, ambCol: [0.72, 0.76, 0.92], ambInt: 0.65, dirCol: [0.78, 0.82, 0.95], dirInt: 1.4, bgDim: 0.60, moonAngle: 0.5 },
  ];

  function lerpTOD(a, b, t) {
    return {
      ambCol: a.ambCol.map((v, i) => v + (b.ambCol[i] - v) * t),
      ambInt: a.ambInt + (b.ambInt - a.ambInt) * t,
      dirCol: a.dirCol.map((v, i) => v + (b.dirCol[i] - v) * t),
      dirInt: a.dirInt + (b.dirInt - a.dirInt) * t,
      bgDim: a.bgDim + (b.bgDim - a.bgDim) * t,
      moonAngle: a.moonAngle + (b.moonAngle - a.moonAngle) * t,
    };
  }

  function getTimeOfDayLighting() {
    const now = new Date();
    const h = now.getHours() + now.getMinutes() / 60;
    let lo = TOD_KEYFRAMES[0];
    let hi = TOD_KEYFRAMES[1];
    for (let i = 0; i < TOD_KEYFRAMES.length - 1; i++) {
      if (h >= TOD_KEYFRAMES[i].hour && h < TOD_KEYFRAMES[i + 1].hour) {
        lo = TOD_KEYFRAMES[i];
        hi = TOD_KEYFRAMES[i + 1];
        break;
      }
    }
    const t = (h - lo.hour) / (hi.hour - lo.hour || 1);
    return lerpTOD(lo, hi, t);
  }

  function isNightTime() {
    if (lightingMode === 'night') return true;
    if (lightingMode === 'day') return false;
    const h = new Date().getHours();
    return h >= 20 || h < 6;
  }

  // Apply initial lighting
  let bgMaterialRef = null; // set later when background is created
  // v1.74. Vanilla bg layer for the subsurface crossfade. Sibling
  // to bgMesh at marginally higher Y, transparent + depthWrite off,
  // opacity driven by _subsurfaceProgress so the bg shifts to the
  // vanilla blur when the player views the subsurface and shifts
  // back on return. Both bg textures stay loaded so the swap is
  // GPU-only and stutter-free.
  let vanillaBgMaterialRef = null;
  let vanillaBgTextureRef = null;
  const initTOD = getTimeOfDayLighting();
  ambient.color.setRGB(...initTOD.ambCol);
  ambient.intensity = initTOD.ambInt;
  directional.color.setRGB(...initTOD.dirCol);
  directional.intensity = initTOD.dirInt;
  if (initTOD.moonAngle > 0) applyMoonPosition(initTOD.moonAngle);

  function applyMoonPosition(moonAngle) {
    // Moon arcs from east (negative X) to west (positive X) across the night sky
    const angle = moonAngle * Math.PI; // 0..1 → 0..π
    const moonX = Math.cos(angle) * 80;
    const moonY = 100;
    const moonZ = Math.sin(angle) * 40 - 20;
    directional.position.set(moonX, moonY, moonZ);
  }

  // ─── Fireflies (visible at night) ───
  const ROAMING_FIREFLY_COUNT = 12;
  const CANDLE_FIREFLY_COUNT = 4; // per candle
  // Subsurface lift distance for fireflies. Matches the card flight
  // in GameBoard (VIEW_TRANSITION_LIFT) so both populations launch
  // and return on the same vertical envelope.
  const SUBSURFACE_LIFT = 70;
  const fireflyStartTime = performance.now() * 0.001;
  const fireflies = [];
  const fireflyGroup = new THREE.Group();
  scene.add(fireflyGroup);

  // ─── Table Candles (at opposite corners, lit at night) ───
  const LAMP_POSITIONS = [
    new THREE.Vector3(-95, 0.05, -64),  // top-left from Player 1's view
    new THREE.Vector3(95, 0.05, 64),    // bottom-right from Player 1's view
  ];
  const lamps = [];
  // `transparent: true` baked in at init so the subsurface fade can
  // ramp candle opacity without forcing a mid-frame shader recompile.
  // While opacity stays at 1 these render identically to opaque
  // materials. Three.js only diverges paths when opacity < 1.
  const candleHolderMat = new THREE.MeshStandardMaterial({
    color: 0x1a1208,
    roughness: 0.55,
    metalness: 0.85,
    transparent: true,
    opacity: 1,
  });
  const candleWaxMat = new THREE.MeshStandardMaterial({
    color: 0xf5e6c8,
    roughness: 0.85,
    transparent: true,
    opacity: 1,
  });

  for (const lampPos of LAMP_POSITIONS) {
    const lampGroup = new THREE.Group();

    // Holder saucer
    const saucer = new THREE.Mesh(
      new THREE.CylinderGeometry(3.2, 3.6, 0.6, 10),
      candleHolderMat,
    );
    saucer.position.y = 0.3;
    saucer.castShadow = true;
    saucer.receiveShadow = true;
    lampGroup.add(saucer);

    // Wax pool around candle base
    const waxPool = new THREE.Mesh(
      new THREE.CylinderGeometry(1.8, 2.0, 0.4, 8),
      candleWaxMat,
    );
    waxPool.position.y = 0.8;
    lampGroup.add(waxPool);

    // Candle body (slightly tapered, thinner at the top)
    const candleBody = new THREE.Mesh(
      new THREE.CylinderGeometry(0.9, 1.1, 6.0, 8),
      candleWaxMat,
    );
    candleBody.position.y = 4.0;
    candleBody.castShadow = true;
    lampGroup.add(candleBody);

    // Flame sprite (tall teardrop glow)
    const flameCanvas = document.createElement('canvas');
    flameCanvas.width = 32;
    flameCanvas.height = 32;
    const fCtx = flameCanvas.getContext('2d');
    const fGrad = fCtx.createRadialGradient(16, 18, 0, 16, 16, 16);
    fGrad.addColorStop(0, 'rgba(255, 255, 220, 1)');
    fGrad.addColorStop(0.2, 'rgba(255, 230, 100, 0.9)');
    fGrad.addColorStop(0.5, 'rgba(255, 180, 40, 0.5)');
    fGrad.addColorStop(1, 'rgba(255, 120, 10, 0)');
    fCtx.fillStyle = fGrad;
    fCtx.fillRect(0, 0, 32, 32);
    const flameTex = new THREE.CanvasTexture(flameCanvas);
    const flameMat = new THREE.SpriteMaterial({
      map: flameTex,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      transparent: true,
      opacity: 0,
    });
    const flameSprite = new THREE.Sprite(flameMat);
    flameSprite.scale.set(2.2, 4.0, 1);
    flameSprite.position.y = 8.5;
    flameSprite.renderOrder = 999;
    lampGroup.add(flameSprite);

    // Soft glow aura around the flame
    const glowCanvas = document.createElement('canvas');
    glowCanvas.width = 64;
    glowCanvas.height = 64;
    const gCtx = glowCanvas.getContext('2d');
    const gGrad = gCtx.createRadialGradient(32, 32, 0, 32, 32, 32);
    gGrad.addColorStop(0, 'rgba(255, 220, 100, 0.9)');
    gGrad.addColorStop(0.4, 'rgba(255, 180, 50, 0.4)');
    gGrad.addColorStop(1, 'rgba(255, 150, 30, 0)');
    gCtx.fillStyle = gGrad;
    gCtx.fillRect(0, 0, 64, 64);
    const glowTex = new THREE.CanvasTexture(glowCanvas);
    const glowSpriteMat = new THREE.SpriteMaterial({
      map: glowTex,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      transparent: true,
      opacity: 0,
    });
    const glowSprite = new THREE.Sprite(glowSpriteMat);
    glowSprite.scale.set(16, 16, 1);
    glowSprite.position.y = 8;
    glowSprite.renderOrder = 999;
    lampGroup.add(glowSprite);

    // Point light at the flame. Stronger, reaches further
    const lampLight = new THREE.PointLight(0xffcc44, 0, 70, 2);
    lampLight.position.y = 8;
    lampGroup.add(lampLight);

    lampGroup.position.copy(lampPos);
    scene.add(lampGroup);

    lamps.push({
      group: lampGroup,
      light: lampLight,
      flameMaterial: flameMat,
      flameSprite,
      glowSpriteMaterial: glowSpriteMat,
      tipX: lampPos.x,
      tipY: lampPos.y + 8,
      tipZ: lampPos.z,
      // v1.74. Intrinsic (day/night-smoothed) values, kept separate
      // from the actual `light.intensity` / `*Material.opacity` so
      // the subsurface fade multiplier can't feed back into the
      // 0.02-per-frame smoother. Initialized to 0 = "off" so the
      // first frame at night ramps up cleanly.
      _intrinsicLightIntensity: 0,
      _intrinsicFlameOp: 0,
      _intrinsicGlowOp: 0,
    });
  }

  function createFirefly() {
    const c = document.createElement('canvas');
    c.width = 32;
    c.height = 32;
    const ctx = c.getContext('2d');
    const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    gradient.addColorStop(0, 'rgba(255, 240, 140, 1)');
    gradient.addColorStop(0.3, 'rgba(255, 220, 80, 0.6)');
    gradient.addColorStop(1, 'rgba(255, 200, 50, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 32, 32);

    const texture = new THREE.CanvasTexture(c);
    const mat = new THREE.SpriteMaterial({
      map: texture,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      transparent: true,
      opacity: 0,
    });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(1.2, 1.2, 1);
    sprite.renderOrder = 999;
    fireflyGroup.add(sprite);
    // No PointLight here. Each firefly used to carry its own light, but
    // 20 fireflies × MeshStandardMaterial fragment shaders meant every
    // PBR pixel looped over 22 lights every frame (the lamps + 20
    // fireflies). The visible glow comes from the additive sprite, not
    // the light contribution, so dropping the lights is invisible to
    // the eye but ~10× cheaper per fragment.
    return { sprite, light: null, material: mat };
  }

  // Candle fireflies. Hover near each candle, with spawn/despawn lifecycle
  for (let li = 0; li < lamps.length; li++) {
    const lamp = lamps[li];
    for (let ci = 0; ci < CANDLE_FIREFLY_COUNT; ci++) {
      const { sprite, light, material } = createFirefly();
      sprite.position.set(lamp.tipX, lamp.tipY, lamp.tipZ);

      const homeAngle = Math.random() * Math.PI * 2;
      fireflies.push({
        sprite, light, material,
        type: 'candle',
        lampX: lamp.tipX, lampY: lamp.tipY, lampZ: lamp.tipZ,
        homeX: lamp.tipX + Math.cos(homeAngle) * (2 + Math.random() * 6),
        homeY: lamp.tipY + (Math.random() - 0.5) * 4,
        homeZ: lamp.tipZ + Math.sin(homeAngle) * (2 + Math.random() * 6),
        baseX: lamp.tipX, baseY: lamp.tipY, baseZ: lamp.tipZ,
        phase: Math.random() * Math.PI * 2,
        speedX: 0.4 + Math.random() * 0.5,
        speedY: 0.3 + Math.random() * 0.3,
        speedZ: 0.4 + Math.random() * 0.5,
        radiusX: 1 + Math.random() * 1.5,
        radiusY: 0.3 + Math.random() * 0.8,
        radiusZ: 1 + Math.random() * 1.5,
        pulseSpeed: 2 + Math.random() * 2,
        pulsePhase: Math.random() * Math.PI * 2,
        spawnAt: fireflyStartTime + ci * 0.8 + Math.random() * 2,
        spawned: false,
        lifetime: 15 + Math.random() * 20,
        respawnedAt: 0,
        fading: false,
        // v1.74. Day/night-smoothed opacity, kept separate from
        // `material.opacity` so the subsurface fade multiplier can't
        // feed back into the 0.02-per-frame smoother.
        _intrinsicOpacity: 0,
      });
    }
  }

  // Roaming fireflies. Emerge from candles one by one, then explore the table
  for (let i = 0; i < ROAMING_FIREFLY_COUNT; i++) {
    const { sprite, light, material } = createFirefly();
    const lamp = lamps[i % lamps.length];
    sprite.position.set(lamp.tipX, lamp.tipY, lamp.tipZ);

    fireflies.push({
      sprite, light, material,
      type: 'roaming',
      lampX: lamp.tipX, lampY: lamp.tipY, lampZ: lamp.tipZ,
      homeX: (Math.random() - 0.5) * TABLE_WIDTH * 0.9,
      homeY: 2 + Math.random() * 8,
      homeZ: (Math.random() - 0.5) * TABLE_HEIGHT * 0.9,
      baseX: lamp.tipX, baseY: lamp.tipY, baseZ: lamp.tipZ,
      phase: Math.random() * Math.PI * 2,
      speedX: 0.3 + Math.random() * 0.4,
      speedY: 0.2 + Math.random() * 0.3,
      speedZ: 0.3 + Math.random() * 0.4,
      radiusX: 1.5 + Math.random() * 2,
      radiusY: 0.5 + Math.random() * 1,
      radiusZ: 1.5 + Math.random() * 2,
      pulseSpeed: 1.5 + Math.random() * 2,
      pulsePhase: Math.random() * Math.PI * 2,
      spawnAt: fireflyStartTime + 2 + i * 1.2 + Math.random() * 3,
      spawned: false,
      nextHomeTime: fireflyStartTime + 25 + Math.random() * 30,
      _intrinsicOpacity: 0,
    });
  }

  // ── Firefly tracker lights ─────────────────────────────────────
  // Instead of 20 individual PointLights (which would loop 20×
  // per PBR pixel), we use 4 shared lights that track the brightest
  // visible fireflies each frame. This gives the warm-glow-on-table
  // effect at a fixed cost of 4 extra light loops per pixel.
  const FIREFLY_TRACKER_COUNT = 2;
  const fireflyTrackerLights = [];
  const _fireflyAvailBuf = []; // reused per-frame to avoid allocations
  for (let i = 0; i < FIREFLY_TRACKER_COUNT; i++) {
    const fl = new THREE.PointLight(0xffdd66, 0, 30, 2);
    fl.position.set(0, -100, 0); // off-screen until assigned
    fl.userData = { assignedFirefly: null };
    scene.add(fl);
    fireflyTrackerLights.push(fl);
  }

  let todUpdateTimer = 0;
  // Lighting mode: 'dynamic' uses real clock, 'day' forces noon,
  // 'night' forces midnight. Set via setLightingMode().
  let lightingMode = 'dynamic';

  function getLightingForMode() {
    if (lightingMode === 'day') {
      // Force noon (hour 12)
      return lerpTOD(TOD_KEYFRAMES[4], TOD_KEYFRAMES[4], 0);
    }
    if (lightingMode === 'night') {
      // Force midnight (hour 0)
      return lerpTOD(TOD_KEYFRAMES[0], TOD_KEYFRAMES[0], 0);
    }
    return getTimeOfDayLighting();
  }

  // Table. Rounded rectangle top surface + thick rounded body
  // battlemapUrl can be either a string (combined art+UI in one
  // texture, e.g. vanilla) or `{ art, uiConfig }` for layered themes
  // (procedural UI overlay generated from uiConfig). The legacy
  // `{ art, ui }` shape with a baked-UI WebP URL is no longer used.
  const initialArtUrl = typeof battlemapUrl === 'string' ? battlemapUrl : battlemapUrl.art;
  const initialUiConfig = typeof battlemapUrl === 'object' ? battlemapUrl.uiConfig : null;
  // Initial art loads through the same budgeted async path as later playmat
  // swaps (loadTextureAsync below) so a 10300px battlemap never decodes past
  // what the display can resolve. Assigned onto topMaterial when ready.
  let tableTexture = null;

  // Rounded rectangle shape for the top surface
  const tableShape = new THREE.Shape();
  const tw = TABLE_WIDTH / 2;
  const th = TABLE_HEIGHT / 2;
  const tr = TABLE_CORNER_RADIUS;
  tableShape.moveTo(-tw + tr, -th);
  tableShape.lineTo(tw - tr, -th);
  tableShape.quadraticCurveTo(tw, -th, tw, -th + tr);
  tableShape.lineTo(tw, th - tr);
  tableShape.quadraticCurveTo(tw, th, tw - tr, th);
  tableShape.lineTo(-tw + tr, th);
  tableShape.quadraticCurveTo(-tw, th, -tw, th - tr);
  tableShape.lineTo(-tw, -th + tr);
  tableShape.quadraticCurveTo(-tw, -th, -tw + tr, -th);

  // ShapeGeometry generates UVs from the shape coordinates
  const topGeometry = new THREE.ShapeGeometry(tableShape);

  // Remap UVs from shape coords (-tw..tw, -th..th) to 0..1
  const uvAttr = topGeometry.attributes.uv;
  const posAttr = topGeometry.attributes.position;
  for (let i = 0; i < posAttr.count; i++) {
    const px = posAttr.getX(i);
    const py = posAttr.getY(i);
    uvAttr.setXY(i, (px / TABLE_WIDTH) + 0.5, (py / TABLE_HEIGHT) + 0.5);
  }
  uvAttr.needsUpdate = true;

  // ── Procedural stone-grain normal map ──────────────────────────
  // Subtle random perturbations that break up the flat surface so
  // light plays across it naturally. The micro-texture of real
  // stone. Without this, any gloss layer creates one uniform blob;
  // with it, highlights scatter into soft dappled patterns.
  const stoneNormalCanvas = document.createElement('canvas');
  stoneNormalCanvas.width = 512;
  stoneNormalCanvas.height = 512;
  const stoneCtx = stoneNormalCanvas.getContext('2d');
  const stoneImg = stoneCtx.createImageData(512, 512);
  for (let i = 0; i < stoneImg.data.length; i += 4) {
    stoneImg.data[i]     = 128 + Math.floor((Math.random() - 0.5) * 10); // R
    stoneImg.data[i + 1] = 128 + Math.floor((Math.random() - 0.5) * 10); // G
    stoneImg.data[i + 2] = 255;                                           // B
    stoneImg.data[i + 3] = 255;
  }
  stoneCtx.putImageData(stoneImg, 0, 0);
  // Blur slightly so the grain isn't pixel-sharp noise
  stoneCtx.filter = 'blur(1px)';
  stoneCtx.drawImage(stoneNormalCanvas, 0, 0);
  stoneCtx.filter = 'none';
  const stoneNormalTex = new THREE.CanvasTexture(stoneNormalCanvas);
  stoneNormalTex.wrapS = THREE.RepeatWrapping;
  stoneNormalTex.wrapT = THREE.RepeatWrapping;
  stoneNormalTex.repeat.set(6, 6);

  // Polished stone / marble surface. The clearcoat is very rough
  // (0.65) so it creates broad, diffuse highlights. Not a sharp
  // specular orb. The normal map scatters the highlight into soft
  // dappled light. Sheen approximates subsurface scattering at
  // grazing angles (light entering the stone).
  const topMaterial = new THREE.MeshPhysicalMaterial({
    map: null,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    roughness: 0.6,
    metalness: 0.0,
    clearcoat: 0.3,
    clearcoatRoughness: 0.65,
    normalMap: stoneNormalTex,
    normalScale: new THREE.Vector2(0.1, 0.1),
    sheen: 0.15,
    sheenRoughness: 0.5,
    sheenColor: new THREE.Color(0x3d2e1f),
    envMapIntensity: 0.4,
  });
  const tableMesh = new THREE.Mesh(topGeometry, topMaterial);
  tableMesh.rotation.x = -Math.PI / 2;
  tableMesh.position.y = 0.05;
  tableMesh.receiveShadow = true;
  tableMesh.userData = { type: 'table' };
  scene.add(tableMesh);

  // Kick off the budgeted initial-art decode (loadTextureAsync is hoisted).
  // setPlaymat may race this on an early theme swap; it owns topMaterial.map
  // from its first assignment, so only fill the map if it is still empty.
  (async () => {
    try {
      const tex = await loadTextureAsync(initialArtUrl);
      if (disposed) { tex.image?.close?.(); tex.dispose(); return; }
      if (topMaterial.map) { tex.image?.close?.(); tex.dispose(); return; }
      tableTexture = tex;
      topMaterial.map = tex;
      topMaterial.needsUpdate = true;
    } catch { /* keep untextured stone surface */ }
  })();

  // Persistent UI overlay for layered playmats (art + transparent UI).
  // Hidden by default. Enabled when a layered theme is active.
  //
  // The material's fragment shader is extended via onBeforeCompile so
  // that any pixel whose UV falls inside one of `uInvertRegions` has
  // its RGB inverted (white→black, etc) at render time. This lets a
  // playmat config flip the UI overlay color over light areas of the
  // artwork without needing a second Figma export. Alpha is left
  // untouched so transparent gaps stay transparent. See
  // PLAYMAT_THEMES.uiInversionRegions for the config-side API.
  const MAX_UI_INVERT_REGIONS = 8;
  const uInvertRegions = { value: new Float32Array(MAX_UI_INVERT_REGIONS * 4) };
  const uInvertRegionCount = { value: 0 };
  // Polygon offsets across the playmat stack MUST differ by whole
  // integers (table -1 → vanilla -2 → ui -3): on Windows, ANGLE maps
  // GL polygonOffset units onto D3D11's integer DepthBias, so a
  // fractional step like -1.5 truncates to the same bias as -1 and
  // the coplanar layers z-fight (striping).
  const uiOverlayMaterial = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 1,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
  });
  uiOverlayMaterial.onBeforeCompile = (shader) => {
    shader.uniforms.uInvertRegions = uInvertRegions;
    shader.uniforms.uInvertRegionCount = uInvertRegionCount;
    // Inject uniform declarations alongside the standard chunk so
    // they're visible across the rest of the fragment shader.
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      `#include <common>
       uniform vec4 uInvertRegions[${MAX_UI_INVERT_REGIONS}];
       uniform int uInvertRegionCount;`
    );
    // Hook into the texture-sampled diffuse color before lighting.
    // vMapUv is the UV after map transform. Loop is bounded by the
    // compile-time MAX so the GLSL compiler can unroll it.
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>',
      `#include <map_fragment>
       {
         bool _vkInv = false;
         for (int i = 0; i < ${MAX_UI_INVERT_REGIONS}; i++) {
           if (i >= uInvertRegionCount) break;
           vec4 r = uInvertRegions[i];
           if (vMapUv.x >= r.x && vMapUv.x <= r.x + r.z &&
               vMapUv.y >= r.y && vMapUv.y <= r.y + r.w) {
             _vkInv = true;
             break;
           }
         }
         if (_vkInv) diffuseColor.rgb = vec3(1.0) - diffuseColor.rgb;
       }`
    );
  };
  // Stable cache key so three.js doesn't re-compile the shader every
  // time the material is touched.
  uiOverlayMaterial.customProgramCacheKey = () => 'vk-ui-overlay-invertable-v1';

  function setUiInvertRegions(regions) {
    const arr = uInvertRegions.value;
    const list = Array.isArray(regions) ? regions : [];
    const n = Math.min(list.length, MAX_UI_INVERT_REGIONS);
    for (let i = 0; i < n; i++) {
      const r = list[i] || {};
      arr[i * 4 + 0] = Math.max(0, Math.min(1, Number(r.x) || 0));
      arr[i * 4 + 1] = Math.max(0, Math.min(1, Number(r.y) || 0));
      arr[i * 4 + 2] = Math.max(0, Math.min(1, Number(r.width) || 0));
      arr[i * 4 + 3] = Math.max(0, Math.min(1, Number(r.height) || 0));
    }
    for (let i = n; i < MAX_UI_INVERT_REGIONS; i++) {
      arr[i * 4 + 0] = 0;
      arr[i * 4 + 1] = 0;
      arr[i * 4 + 2] = 0;
      arr[i * 4 + 3] = 0;
    }
    uInvertRegionCount.value = n;
  }
  const uiOverlayMesh = new THREE.Mesh(topGeometry, uiOverlayMaterial);
  uiOverlayMesh.rotation.x = -Math.PI / 2;
  uiOverlayMesh.position.y = 0.06;
  uiOverlayMesh.renderOrder = 1;
  if (initialUiConfig) {
    // Bind the procedural UI texture asynchronously. The generator
    // composites SVG components + grid into a CanvasTexture and
    // caches the result. Mesh stays hidden until the texture is
    // ready, then flips visible in the next frame. Also seed
    // setPlaymat's _lastUi cache so the editor's "skip components"
    // toggle has a config to round-trip when reopened later.
    setPlaymat._lastUi = JSON.stringify(initialUiConfig);
    uiOverlayMesh.visible = false;
    generatePlaymatUiTexture(initialUiConfig).then((uiTex) => {
      uiOverlayMaterial.map = uiTex;
      uiOverlayMaterial.needsUpdate = true;
      uiOverlayMesh.visible = true;
    }).catch((err) => {
      console.warn('[tableScene] initial UI overlay generation failed:', err?.message || err);
    });
  } else {
    uiOverlayMesh.visible = false;
  }
  scene.add(uiOverlayMesh);

  // v1.74. Vanilla underlay for the subsurface region view. Sits
  // ABOVE the user's chosen playmat (tableMesh) and BELOW the UI
  // overlay, fully transparent by default. When the player toggles
  // to the subsurface view, GameBoard drives its opacity 0→1 via
  // setSubsurfaceFade, crossfading from the user's playmat to the
  // vanilla Valkenhall playmat. Both textures stay loaded so the
  // crossfade is GPU-cheap and stutter-free.
  //
  // PBR settings mirror topMaterial so the vanilla layer matches
  // the table's marble/clearcoat look when fully opaque. depthWrite
  // off so cards above continue depth-testing against the opaque
  // tableMesh.
  const vanillaOverlayMaterial = new THREE.MeshPhysicalMaterial({
    transparent: true,
    opacity: 0,
    depthWrite: false,
    polygonOffset: true,
    // Whole-integer step above the table's -1. See the DepthBias
    // note on uiOverlayMaterial.
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    roughness: 0.6,
    metalness: 0.0,
    clearcoat: 0.3,
    clearcoatRoughness: 0.65,
    normalMap: stoneNormalTex,
    normalScale: new THREE.Vector2(0.1, 0.1),
    sheen: 0.15,
    sheenRoughness: 0.5,
    sheenColor: new THREE.Color(0x3d2e1f),
    envMapIntensity: 0.4,
  });
  const vanillaOverlayMesh = new THREE.Mesh(topGeometry, vanillaOverlayMaterial);
  vanillaOverlayMesh.rotation.x = -Math.PI / 2;
  // 0.005 above the table (not a hair like 0.0001): at gameplay
  // distance (~160 units, near 1 / far 1000) one 24-bit depth step is
  // ~0.0015 world units, so the gap must span several steps to be
  // visible to the depth test at all. Stays below uiOverlayMesh (0.06).
  vanillaOverlayMesh.position.y = 0.055;
  vanillaOverlayMesh.receiveShadow = true;
  // Between tableMesh (renderOrder default 0) and uiOverlayMesh (1)
  // so transparent ordering is: table → vanilla overlay → ui overlay
  // → cards. Vanilla covers custom art when opaque; UI overlay's
  // own opacity is faded to 0 in lockstep so the vanilla baked-in
  // grid is what shows in subsurface.
  vanillaOverlayMesh.renderOrder = 0.5;
  scene.add(vanillaOverlayMesh);

  // Thick rounded body underneath (ExtrudeGeometry. No texture needed, just solid color)
  const bodyGeometry = new THREE.ExtrudeGeometry(tableShape, {
    depth: TABLE_THICKNESS,
    bevelEnabled: false,
    steps: 1,
  });
  const bodySideMaterial = new THREE.MeshStandardMaterial({ color: 0x1a1510, roughness: 0.9 });
  const bodyMesh = new THREE.Mesh(bodyGeometry, bodySideMaterial);
  bodyMesh.rotation.x = -Math.PI / 2;
  bodyMesh.position.y = -TABLE_THICKNESS;
  bodyMesh.receiveShadow = true;
  bodyMesh.castShadow = true;
  scene.add(bodyMesh);

  // Drifting cloud layers between the table and background
  const cloudLayers = [];
  const CLOUD_PLANE_SIZE = 400;
  const cloudConfigs = [
    { y: -TABLE_THICKNESS - 2, speed: 0.0008, dir: [1, 0.3], opacity: 0.18, scale: 1.0 },
    { y: -TABLE_THICKNESS - 4, speed: 0.0005, dir: [-0.6, 1], opacity: 0.14, scale: 0.7 },
    { y: -TABLE_THICKNESS - 6, speed: 0.0003, dir: [0.4, -0.8], opacity: 0.10, scale: 0.5 },
  ];

  const cloudVertexShader = `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;

  // Simplex-style noise + fbm for natural cloud shapes
  const cloudFragmentShader = `
    uniform float uTime;
    uniform float uOpacity;
    uniform float uScale;
    uniform vec2 uDirection;
    varying vec2 vUv;

    // Simple hash-based noise
    vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }

    float snoise(vec2 v) {
      const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                         -0.577350269189626, 0.024390243902439);
      vec2 i  = floor(v + dot(v, C.yy));
      vec2 x0 = v - i + dot(i, C.xx);
      vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
      vec4 x12 = x0.xyxy + C.xxzz;
      x12.xy -= i1;
      i = mod289(i);
      vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0))
                                   + i.x + vec3(0.0, i1.x, 1.0));
      vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
      m = m * m;
      m = m * m;
      vec3 x = 2.0 * fract(p * C.www) - 1.0;
      vec3 h = abs(x) - 0.5;
      vec3 ox = floor(x + 0.5);
      vec3 a0 = x - ox;
      m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);
      vec3 g;
      g.x = a0.x * x0.x + h.x * x0.y;
      g.yz = a0.yz * x12.xz + h.yz * x12.yw;
      return 130.0 * dot(m, g);
    }

    float fbm(vec2 p) {
      float value = 0.0;
      float amplitude = 0.5;
      for (int i = 0; i < 5; i++) {
        value += amplitude * snoise(p);
        p *= 2.0;
        amplitude *= 0.5;
      }
      return value;
    }

    void main() {
      vec2 uv = vUv * 3.0 * uScale + uDirection * uTime;
      float n = fbm(uv);
      float cloud = smoothstep(-0.1, 0.4, n);

      // Fade at edges so clouds don't cut off abruptly
      float edgeFade = smoothstep(0.0, 0.15, vUv.x) * smoothstep(1.0, 0.85, vUv.x)
                     * smoothstep(0.0, 0.15, vUv.y) * smoothstep(1.0, 0.85, vUv.y);

      gl_FragColor = vec4(0.85, 0.88, 0.95, cloud * uOpacity * edgeFade);
    }
  `;

  for (const cfg of cloudConfigs) {
    const cloudMat = new THREE.ShaderMaterial({
      vertexShader: cloudVertexShader,
      fragmentShader: cloudFragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uOpacity: { value: cfg.opacity },
        uScale: { value: cfg.scale },
        uDirection: { value: new THREE.Vector2(cfg.dir[0], cfg.dir[1]) },
      },
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const cloudGeo = new THREE.PlaneGeometry(CLOUD_PLANE_SIZE, CLOUD_PLANE_SIZE);
    const cloudMesh = new THREE.Mesh(cloudGeo, cloudMat);
    cloudMesh.rotation.x = -Math.PI / 2;
    cloudMesh.position.y = cfg.y;
    scene.add(cloudMesh);
    cloudLayers.push({ mesh: cloudMesh, material: cloudMat, speed: cfg.speed });
  }

  // Background art plane beneath the table (soft focus via downsampled canvas)
  let bgTextureRef = null;
  if (backgroundUrl) {
    const FLOOR_SIZE = 500;
    const bgGeometry = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE);
    const bgMaterial = new THREE.MeshBasicMaterial({ color: 0x888888 });
    bgMaterialRef = bgMaterial;
    const bgMesh = new THREE.Mesh(bgGeometry, bgMaterial);
    bgMesh.rotation.x = -Math.PI / 2;
    bgMesh.position.y = -TABLE_THICKNESS - 8;
    scene.add(bgMesh);

    // v1.74. Vanilla bg overlay. Same geometry, marginally higher Y
    // so it renders cleanly on top of the user's bg via the
    // transparent pass. opacity = _subsurfaceProgress driven by
    // setSubsurfaceState; lighting (color dimming) follows the
    // primary bg in lockstep so they read consistently at night.
    const vanillaBgMaterial = new THREE.MeshBasicMaterial({
      color: 0x888888,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    vanillaBgMaterialRef = vanillaBgMaterial;
    const vanillaBgMesh = new THREE.Mesh(bgGeometry, vanillaBgMaterial);
    vanillaBgMesh.rotation.x = -Math.PI / 2;
    vanillaBgMesh.position.y = -TABLE_THICKNESS - 7.99;
    scene.add(vanillaBgMesh);

    const bgImage = new Image();
    bgImage.crossOrigin = 'anonymous';
    bgImage.onload = () => {
      if (disposed) return; // scene already torn down
      const blurCanvas = document.createElement('canvas');
      blurCanvas.width = 512;
      blurCanvas.height = 512;
      const ctx = blurCanvas.getContext('2d');
      ctx.filter = 'blur(4px)';
      ctx.drawImage(bgImage, 0, 0, 512, 512);
      bgTextureRef = new THREE.CanvasTexture(blurCanvas);
      bgTextureRef.colorSpace = THREE.SRGBColorSpace;
      bgMaterial.map = bgTextureRef;
      bgMaterial.needsUpdate = true;
    };
    bgImage.src = backgroundUrl;
  }

  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  const tablePlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

  // ── Post-processing pipeline ─────────────────────────────
  // All effects are composed into as few fullscreen passes as
  // possible by pmndrs/postprocessing's uber-shader merging.
  // N8AO gets its own pass (it needs depth+normals); Bloom +
  // Vignette + ToneMapping are merged into a single EffectPass.
  const composer = new EffectComposer(renderer, { frameBufferType: HalfFloatType });
  composer.addPass(new RenderPass(scene, camera));

  const n8ao = new N8AOPostPass(scene, camera, canvas.clientWidth, canvas.clientHeight);
  n8ao.configuration.aoRadius = 5.0;
  n8ao.configuration.distanceFalloff = 1.0;
  n8ao.configuration.intensity = 2.5;
  n8ao.configuration.halfRes = true;
  n8ao.setQualityMode('Low');
  composer.addPass(n8ao);

  // SelectiveBloomEffect (inverted) blooms the whole scene EXCEPT the foil card
  // meshes, which are synced into bloom.selection each frame (getFoilMeshes).
  // Foils carry their own bright metal/holo highlights and must match the
  // un-bloomed deck-builder look, so the scene bloom must not wash them.
  const bloomEffect = new SelectiveBloomEffect(scene, camera, {
    mipmapBlur: true,
    intensity: 0.4,
    luminanceThreshold: 0.88,
    luminanceSmoothing: 0.025,
    radius: 0.8,
    levels: 5,
  });
  bloomEffect.inverted = true;
  const vignetteEffect = new VignetteEffect({ offset: 0.55, darkness: 0.45 });
  const smaaEffect = new SMAAEffect({ preset: SMAAPreset.MEDIUM });
  composer.addPass(new EffectPass(camera, bloomEffect, vignetteEffect, smaaEffect));

  // Replay: the feed sidebar overlays the left fraction of the canvas, so
  // the scene's optical center shifts into the visible remainder via a
  // projection view offset (project/unproject stay consistent, so the DOM
  // card overlays follow for free), and the camera zooms out slightly so the
  // whole battlefield fits the narrower visible width. Pixel-based, so it is
  // reapplied on every resize.
  let viewInsetLeftFrac = 0;
  function applyViewInset() {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (viewInsetLeftFrac > 0) {
      camera.setViewOffset(w, h, -(viewInsetLeftFrac / 2) * w, 0, w, h);
      camera.zoom = 0.86;
    } else {
      camera.clearViewOffset();
      camera.zoom = 1;
    }
  }

  function resize() {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    renderer.setSize(w, h, false);
    composer.setSize(w, h);
    camera.aspect = w / h;
    applyViewInset();
    camera.updateProjectionMatrix();
  }

  resize();

  // Apply initial post-processing toggle state from saved settings. Phones
  // force AO + bloom off regardless of settings: their per-frame fullscreen
  // passes are the dominant GPU cost on mobile chips. (Their render targets
  // are allocated at construction either way; skipping construction entirely
  // is the follow-up if match-on-phone ever needs the memory back.)
  const phone = isPhoneLayout();
  function applyPostProcessingSettings() {
    const s = getGraphicsSettings();
    n8ao.enabled = !phone && s.ssao !== false;
    bloomEffect.blendMode.setBlendFunction(!phone && s.bloom !== false ? 1 : 0); // 1=NORMAL, 0=SKIP
    vignetteEffect.blendMode.setBlendFunction(s.vignette !== false ? 1 : 0);
    smaaEffect.blendMode.setBlendFunction(s.antialiasing !== false ? 1 : 0);
  }

  // Precompile every post-processing shader variant up-front. pmndrs
  // EffectPass bakes effects into a single uber-shader, so toggling
  // any effect at runtime rebuilds it. On Windows/ANGLE this is a
  // synchronous GLSL→HLSL→D3D compile that blocks the GPU process for
  // hundreds of ms per variant. Walking all 2^4 combos once at init
  // caches them in GrShaderCache; subsequent toggles are instant.
  function warmupPostProcessingShaders() {
    renderer.compile(scene, camera);
    for (let mask = 0; mask < 16; mask++) {
      n8ao.enabled = !!(mask & 1);
      bloomEffect.blendMode.setBlendFunction((mask & 2) ? 1 : 0);
      vignetteEffect.blendMode.setBlendFunction((mask & 4) ? 1 : 0);
      smaaEffect.blendMode.setBlendFunction((mask & 8) ? 1 : 0);
      composer.render();
    }
  }
  warmupPostProcessingShaders();
  applyPostProcessingSettings();

  // Live-apply graphics setting changes from the Settings screen so users
  // can preview render-quality tradeoffs without leaving the game.
  const unsubscribeGraphics = onGraphicsChange(() => {
    renderer.setPixelRatio(getEffectivePixelRatio());
    applyPostProcessingSettings();
    resize();
  });

  let animationId = null;
  const heldKeys = new Set();
  const PAN_SPEED = 1.3;

  // External per-frame callbacks (e.g. physics step). Run once per
  // requestAnimationFrame, after pan input but before lighting/cloud
  // updates so positional changes are visible the same frame.
  const frameCallbacks = new Set();
  function onFrame(cb) {
    frameCallbacks.add(cb);
    return () => frameCallbacks.delete(cb);
  }

  // v1.74. Single source of truth for "are we (transitioning into /
  // sitting in) subsurface view?" Read every frame by the candle and
  // firefly tickers below to compute their own fade / lift; written
  // once per frame by GameBoard via setSubsurfaceState (defined later
  // in this file). Declared HERE. Before the per-frame loop. So the
  // loop's reads can never hit a temporal-dead-zone error if the
  // loop ticks before construction reaches the setSubsurfaceState
  // block.
  let _subsurfaceProgress = 0;
  let _subsurfaceTransition = null;

  let renderPaused = false;
  let lastFrameTime = performance.now();
  function animate() {
    animationId = requestAnimationFrame(animate);
    const animateMark = perf.beginMark('frame.animate');
    const nowMs = performance.now();
    const dt = Math.min(0.1, (nowMs - lastFrameTime) / 1000);
    lastFrameTime = nowMs;

    if (heldKeys.size > 0) {
      let inputForward = 0;
      let inputRight = 0;
      if (heldKeys.has('w') || heldKeys.has('W')) inputForward += PAN_SPEED;
      if (heldKeys.has('s') || heldKeys.has('S')) inputForward -= PAN_SPEED;
      if (heldKeys.has('d') || heldKeys.has('D')) inputRight += PAN_SPEED;
      if (heldKeys.has('a') || heldKeys.has('A')) inputRight -= PAN_SPEED;
      if (inputForward !== 0 || inputRight !== 0) {
        // Project input into world space via the camera's horizontal
        // orientation. Same technique as the mouse-drag pan. So WASD
        // always moves relative to the viewer's perspective even when
        // the board is flipped for player 2.
        camera.getWorldDirection(_forward);
        _right.crossVectors(_forward, camera.up).normalize();
        _forward.crossVectors(camera.up, _right).normalize();
        // Scale WASD speed with zoom: constant screen-relative speed,
        // not constant world speed (which races when zoomed in).
        const wasdScale = orbitDistanceGoal / 160;
        orbitTargetGoal.addScaledVector(_right, inputRight * wasdScale);
        orbitTargetGoal.addScaledVector(_forward, inputForward * wasdScale);
        orbitTargetGoal.y = 0;
        clampPanTarget();
      }
    }

    // Ease the camera toward the input goal before the frame callbacks
    // run, so overlay projections in those callbacks see the final
    // camera for this frame (no one-frame DOM-vs-WebGL lag).
    const camK = 1 - Math.exp(-dt * 14);
    orbitTarget.lerp(orbitTargetGoal, camK);
    orbitDistance += (orbitDistanceGoal - orbitDistance) * camK;
    if (orbitTarget.distanceToSquared(orbitTargetGoal) < 1e-6) orbitTarget.copy(orbitTargetGoal);
    if (Math.abs(orbitDistanceGoal - orbitDistance) < 1e-3) orbitDistance = orbitDistanceGoal;
    updateCameraFromOrbit();

    const callbacksMark = perf.beginMark('frame.callbacks');
    for (const cb of frameCallbacks) {
      try { cb(dt); } catch (err) { console.error('frame cb error:', err); }
    }
    perf.endMark(callbacksMark);

    // Animate cloud drift (wrap at 10000 to prevent float precision loss over long sessions)
    for (const layer of cloudLayers) {
      layer.material.uniforms.uTime.value = (layer.material.uniforms.uTime.value + layer.speed) % 10000;
    }

    // Update time-of-day lighting every ~2 seconds (no need for per-frame)
    todUpdateTimer++;
    if (todUpdateTimer >= 120) {
      todUpdateTimer = 0;
      const tod = getLightingForMode();
      ambient.color.setRGB(...tod.ambCol);
      ambient.intensity = tod.ambInt;
      directional.color.setRGB(...tod.dirCol);
      directional.intensity = tod.dirInt;

      // Darken the background art at night (table stays bright).
      // Apply to both bg layers so the vanilla bg overlay matches
      // the primary bg's lighting and the subsurface crossfade reads
      // as a clean swap rather than a brightness flicker.
      if (bgMaterialRef) {
        const d = tod.bgDim;
        bgMaterialRef.color.setRGB(d, d, d);
        vanillaBgMaterialRef?.color.setRGB(d, d, d);
      }

      // Move directional light to simulate moon arc at night
      if (tod.moonAngle > 0) {
        applyMoonPosition(tod.moonAngle);
      } else {
        directional.position.set(40, 70, 25); // daytime default
      }
    }

    // Get mouse world position on table plane for firefly scaring
    const mouseWorld = _raycastTarget;
    raycaster.setFromCamera(mouse, camera);
    raycaster.ray.intersectPlane(tablePlane, mouseWorld);

    // Animate fireflies
    const now = performance.now() * 0.001;
    const night = isNightTime();

    const lampsMark = perf.beginMark('frame.lamps');
    // Animate candle flame and glow with day/night. The day/night
    // smoother converges over ~1.6s, which is slower than the
    // subsurface transition (850ms). So we keep the smoothed value
    // in `_intrinsic*` fields and multiply by the subsurface factor
    // only at display time. The tween never sees the post-multiplied
    // value, which is what avoids a feedback loop that would freeze
    // the candle at low intensity for seconds after returning to
    // surface.
    const candleSubsurfaceVis = 1 - _subsurfaceProgress;
    for (let li = 0; li < lamps.length; li++) {
      const lamp = lamps[li];
      const candleLightOn = getGraphicsSettings().candleLight !== false;
      const targetIntensity = (night && candleLightOn) ? 200 : 0;
      lamp._intrinsicLightIntensity += (targetIntensity - lamp._intrinsicLightIntensity) * 0.02;
      let flickerFactor = 1;
      if (night) {
        flickerFactor = 0.93 + 0.07 * Math.sin(now * 4.5 + li * 2.1);
        // Wobble the flame sprite gently
        lamp.flameSprite.position.x = Math.sin(now * 5.3 + li * 3) * 0.15;
        lamp.flameSprite.position.z = Math.cos(now * 4.1 + li * 1.7) * 0.1;
      }
      const targetFlameOp = night ? 0.9 : 0;
      lamp._intrinsicFlameOp += (targetFlameOp - lamp._intrinsicFlameOp) * 0.02;
      const targetGlowOp = night ? 0.6 : 0;
      lamp._intrinsicGlowOp += (targetGlowOp - lamp._intrinsicGlowOp) * 0.02;

      // Display = intrinsic × flicker × subsurface visibility. v1.74
      // The candles fade out (no motion) while the player views
      // the subsurface; they fade back in lockstep with the rest of
      // the surface↔subsurface transition.
      lamp.light.intensity = lamp._intrinsicLightIntensity * flickerFactor * candleSubsurfaceVis;
      lamp.flameMaterial.opacity = lamp._intrinsicFlameOp * candleSubsurfaceVis;
      lamp.glowSpriteMaterial.opacity = lamp._intrinsicGlowOp * candleSubsurfaceVis;
    }
    // Solid candle parts (holder + wax + body) share two materials
    // across both lamps; write each one once per frame. Their
    // intrinsic opacity is always 1, so the display value IS the
    // subsurface visibility.
    candleHolderMat.opacity = candleSubsurfaceVis;
    candleWaxMat.opacity = candleSubsurfaceVis;

    perf.endMark(lampsMark);
    perf.gauge('scene.lamps', lamps.length);
    perf.gauge('scene.fireflies', fireflies.length);

    const firefliesMark = perf.beginMark('frame.fireflies');
    const SCARE_RADIUS = 25;
    const FLEE_SPEED = 0.15;

    for (const ff of fireflies) {
      // Staggered spawn. Invisible until spawn time arrives
      if (!ff.spawned) {
        if (now < ff.spawnAt) {
          ff._intrinsicOpacity = 0;
          ff.material.opacity = 0;
          continue;
        }
        ff.spawned = true;
        ff.respawnedAt = now;
        ff.baseX = ff.lampX;
        ff.baseY = ff.lampY;
        ff.baseZ = ff.lampZ;
      }

      // Determine target opacity (night vs day, plus lifecycle for candle type)
      let targetOp = night ? 0.7 : 0;

      if (ff.type === 'candle') {
        // Lifecycle: fade out after lifetime, then respawn at candle
        if (!ff.fading && now - ff.respawnedAt > ff.lifetime) {
          ff.fading = true;
        }
        if (ff.fading) {
          targetOp = 0;
          if (ff._intrinsicOpacity < 0.01) {
            ff.fading = false;
            ff.respawnedAt = now;
            ff.lifetime = 15 + Math.random() * 20;
            ff.baseX = ff.lampX;
            ff.baseY = ff.lampY;
            ff.baseZ = ff.lampZ;
            const a = Math.random() * Math.PI * 2;
            ff.homeX = ff.lampX + Math.cos(a) * (2 + Math.random() * 6);
            ff.homeY = ff.lampY + (Math.random() - 0.5) * 4;
            ff.homeZ = ff.lampZ + Math.sin(a) * (2 + Math.random() * 6);
          }
        }
      }

      // Roaming fireflies periodically pick a new wandering target
      if (ff.type === 'roaming' && now > ff.nextHomeTime) {
        ff.homeX = (Math.random() - 0.5) * TABLE_WIDTH * 0.9;
        ff.homeY = 2 + Math.random() * 8;
        ff.homeZ = (Math.random() - 0.5) * TABLE_HEIGHT * 0.9;
        ff.nextHomeTime = now + 20 + Math.random() * 30;
      }

      // Smooth opacity transition (intrinsic. Subsurface fade is
      // applied separately at the end of the loop so the smoother
      // never sees the post-multiplied value).
      ff._intrinsicOpacity += (targetOp - ff._intrinsicOpacity) * 0.02;

      // Check distance to mouse cursor (XZ plane)
      const dx = ff.sprite.position.x - mouseWorld.x;
      const dz = ff.sprite.position.z - mouseWorld.z;
      const distSq = dx * dx + dz * dz;

      if (distSq < SCARE_RADIUS * SCARE_RADIUS && distSq > 0.01) {
        const dist = Math.sqrt(distSq);
        const fleeStrength = FLEE_SPEED * (1 - dist / SCARE_RADIUS);

        // Candle fireflies or roaming near a candle. Flee toward candle
        const distToLampSq = (ff.baseX - ff.lampX) ** 2 + (ff.baseZ - ff.lampZ) ** 2;
        if (ff.type === 'candle' || distToLampSq < 30 * 30) {
          const toLampX = ff.lampX - ff.baseX;
          const toLampY = ff.lampY - ff.baseY;
          const toLampZ = ff.lampZ - ff.baseZ;
          const toLampDist = Math.sqrt(toLampX * toLampX + toLampY * toLampY + toLampZ * toLampZ);
          if (toLampDist > 0.5) {
            ff.baseX += (toLampX / toLampDist) * fleeStrength * 5;
            ff.baseY += (toLampY / toLampDist) * fleeStrength * 5;
            ff.baseZ += (toLampZ / toLampDist) * fleeStrength * 5;
          }
        } else {
          ff.baseX += (dx / dist) * fleeStrength * 3;
          ff.baseZ += (dz / dist) * fleeStrength * 3;
          ff.baseY += fleeStrength * 2;
        }
        ff._intrinsicOpacity *= 0.92;
      } else {
        // Drift toward home. Candle fireflies settle faster, roaming drift slowly
        const drift = ff.type === 'candle' ? 0.008 : 0.002;
        ff.baseX += (ff.homeX - ff.baseX) * drift;
        ff.baseY += (ff.homeY - ff.baseY) * drift;
        ff.baseZ += (ff.homeZ - ff.baseZ) * drift;
      }

      // Wandering motion around base
      const t = now + ff.phase;
      ff.sprite.position.x = ff.baseX + Math.sin(t * ff.speedX) * ff.radiusX;
      ff.sprite.position.y = ff.baseY + Math.sin(t * ff.speedY + 1.3) * ff.radiusY;
      ff.sprite.position.z = ff.baseZ + Math.cos(t * ff.speedZ + 0.7) * ff.radiusZ;

      // Pulsing glow
      const pulse = 0.4 + 0.6 * (0.5 + 0.5 * Math.sin(t * ff.pulseSpeed + ff.pulsePhase));
      ff.sprite.scale.setScalar(1.2 * pulse);

      // v1.74. Subsurface lift + fade. Applied AFTER the wandering
      // simulation writes the firefly's natural position, so the
      // underlying motion data stays pristine. When the player
      // returns to surface, the firefly resumes from exactly where
      // the simulation placed it. Same curves and per-firefly
      // staggered delay as the card flight so the playmat reads as
      // one cohesive flock taking off and returning.
      //
      //   out lift:    1 - (1-localT)^3   opacity: 1 - localT^4
      //   in  lift:    (1-localT)^3       opacity: 1 - (1-localT)^4
      //
      // At rest in subsurface, fireflies sit at full lift / opacity 0.
      // Displayed opacity = intrinsic (day/night smoothed) × subsurface curve.
      const sTrans = _subsurfaceTransition;
      let subsurfaceOpacityMul = 1;
      let subsurfaceLift = 0;
      if (sTrans) {
        const elapsedMs = performance.now() - sTrans.startTime;
        const localT = Math.max(0, Math.min(1,
          (elapsedMs - (ff._subsurfaceDelay || 0)) / sTrans.duration,
        ));
        const oneMinusT = 1 - localT;
        const oneMinusTCub = oneMinusT * oneMinusT * oneMinusT;
        const oneMinusTQ4  = oneMinusT * oneMinusT * oneMinusT * oneMinusT;
        const isOut = sTrans.target === 'subsurface';
        const liftCurve = isOut ? (1 - oneMinusTCub) : oneMinusTCub;
        subsurfaceOpacityMul = isOut
          ? (1 - localT * localT * localT * localT)
          : (1 - oneMinusTQ4);
        subsurfaceLift = liftCurve * SUBSURFACE_LIFT;
      } else if (_subsurfaceProgress > 0.999) {
        // At-rest subsurface: lifted away, invisible. Simulation
        // keeps running underneath so the return looks organic.
        subsurfaceOpacityMul = 0;
        subsurfaceLift = SUBSURFACE_LIFT;
      }
      ff.sprite.position.y += subsurfaceLift;
      ff.material.opacity = ff._intrinsicOpacity * subsurfaceOpacityMul;
    }

    // Update tracker lights. Each light is ASSIGNED to a specific
    // firefly and sticks with it until that firefly dims out. Only
    // then does the tracker release and pick a new bright firefly.
    // This prevents lights from sliding across the table when the
    // sorted brightness order shuffles.
    const s = getGraphicsSettings();
    const trackersEnabled = s.candleLight !== false && night;
    if (trackersEnabled) {
      const claimed = new Set();
      // Phase 1: keep existing assignments if the firefly is still visible
      for (const fl of fireflyTrackerLights) {
        const assigned = fl.userData.assignedFirefly;
        if (assigned && assigned.material.opacity > 0.05) {
          claimed.add(assigned);
        } else {
          fl.userData.assignedFirefly = null;
        }
      }
      // Phase 2: unassigned trackers claim the brightest unclaimed fireflies
      // Reuse a single array to avoid per-frame allocations.
      const available = _fireflyAvailBuf;
      available.length = 0;
      for (let fi = 0; fi < fireflies.length; fi++) {
        const ff = fireflies[fi];
        if (ff.material.opacity > 0.15 && !claimed.has(ff)) available.push(ff);
      }
      if (available.length > 1) available.sort((a, b) => b.material.opacity - a.material.opacity);
      let nextAvail = 0;
      for (const fl of fireflyTrackerLights) {
        if (!fl.userData.assignedFirefly && nextAvail < available.length) {
          fl.userData.assignedFirefly = available[nextAvail++];
        }
      }
      // Phase 3: update position and intensity from assigned firefly
      for (const fl of fireflyTrackerLights) {
        const ff = fl.userData.assignedFirefly;
        if (ff) {
          fl.position.lerp(ff.sprite.position, 0.12);
          fl.intensity += (ff.material.opacity * 40 - fl.intensity) * 0.06;
        } else {
          fl.intensity += (0 - fl.intensity) * 0.03;
        }
      }
    } else {
      for (const fl of fireflyTrackerLights) {
        fl.intensity += (0 - fl.intensity) * 0.03;
        fl.userData.assignedFirefly = null;
      }
    }

    perf.endMark(firefliesMark);

    const tweensMark = perf.beginMark('frame.tweens');
    updateTweens();
    perf.endMark(tweensMark);

    const foilMark = perf.beginMark('frame.foilSheen');
    updateFoilSheens(1 / 60, camera, directional, ambient);
    // Keep the bloom's exclusion set in sync with the live foil meshes (pruned
    // by updateFoilSheens above) so foils stay out of the scene bloom. Only
    // rebuild the Set when the foil mesh set actually changed (foil card added
    // or pruned). Otherwise this allocated a new array + rebuilt the Set every
    // frame for an unchanged set.
    if (consumeFoilMeshesDirty()) bloomEffect.selection.set(getFoilMeshes());
    perf.endMark(foilMark);

    const renderMark = perf.beginMark('frame.render');
    if (!renderPaused) composer.render();
    perf.endMark(renderMark);

    perf.endMark(animateMark);
  }

  animate();

  function updateMouse(event) {
    const rect = canvas.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  function raycastObjects(event, objects) {
    updateMouse(event);
    raycaster.setFromCamera(mouse, camera);
    return raycaster.intersectObjects(objects, true);
  }

  function raycastTablePoint(event) {
    updateMouse(event);
    raycaster.setFromCamera(mouse, camera);
    const target = _raycastTarget;
    raycaster.ray.intersectPlane(tablePlane, target);
    return target;
  }

  // Camera controls
  let isPanning = false;
  const panGrabWorld = new THREE.Vector3();

  function onWheel(event) {
    event.preventDefault();

    // In mouse mode, scroll wheel always zooms.
    // In trackpad mode, plain scroll pans; Ctrl/Cmd + scroll (pinch) zooms.
    const mouseMode = getInputMode() === 'mouse';
    const isZoom = event.ctrlKey || event.metaKey || mouseMode;

    if (isZoom) {
      // Scale the step by deltaY magnitude instead of a fixed 10% per
      // event: a mouse-wheel notch is one ~100-delta event (≈ the old
      // step), but a trackpad pinch streams many small-delta events,
      // which made the fixed step race.
      const delta = Math.exp(event.deltaY * 0.001);
      orbitDistanceGoal = Math.max(20, Math.min(160, orbitDistanceGoal * delta));
      return;
    }

    // Two-finger trackpad pan. Use camera-relative directions so
    // the movement feels natural regardless of orbit angle.
    const right = _right;
    const forward = _forward;
    camera.getWorldDirection(forward);
    right.crossVectors(forward, camera.up).normalize();
    forward.crossVectors(camera.up, right).normalize();

    // Scale scroll-pan with zoom so a wheel tick covers the same
    // fraction of the visible table at every distance.
    const panScale = 0.15 * (orbitDistanceGoal / 160);
    orbitTargetGoal.addScaledVector(right, event.deltaX * panScale);
    orbitTargetGoal.addScaledVector(forward, -event.deltaY * panScale);
    orbitTargetGoal.y = 0;
    clampPanTarget();
  }

  function clampPanTarget() {
    // Pan limits scale with zoom. Zoomed out (160) keeps tight limits
    // (quarter table), zoomed in (20) allows reaching the full edges.
    // The zoom factor goes from 0 (max out) to 1 (max in).
    const zoomFactor = 1 - (orbitDistanceGoal - 20) / (160 - 20);
    const limitX = TABLE_WIDTH * (0.25 + zoomFactor * 0.25);
    const limitZ = TABLE_HEIGHT * (0.25 + zoomFactor * 0.25);
    orbitTargetGoal.x = Math.max(-limitX, Math.min(limitX, orbitTargetGoal.x));
    orbitTargetGoal.z = Math.max(-limitZ, Math.min(limitZ, orbitTargetGoal.z));
  }

  function onMouseDown(event) {
    // Right mouse = pan
    if (event.button === 2) {
      isPanning = true;
      panGrabWorld.copy(raycastTablePoint(event));
      event.preventDefault();
      return;
    }
  }

  function onMouseMove(event) {
    if (isPanning) {
      // Grab-the-table pan: the world point under the cursor at
      // mousedown stays pinned under the cursor. Raycasting the table
      // plane per event makes sensitivity and direction exact at any
      // zoom and tilt; the old fixed px→world factor felt far too fast
      // zoomed in and drifted against the cursor at the tilted angle.
      // Basing the goal on the live orbitTarget (not the goal) cancels
      // the glide lag exactly: goal = actual + (grab − underCursor).
      const cur = raycastTablePoint(event);
      orbitTargetGoal.copy(orbitTarget).add(panGrabWorld).sub(cur);
      orbitTargetGoal.y = 0;
      clampPanTarget();
      return;
    }
  }

  function onMouseUp(event) {
    if (event.button === 2) isPanning = false;
  }

  // Touch camera controls. Two-finger pinch zooms (same orbit-distance
  // goal as wheel zoom), two-finger drag pans. preventDefault matters as
  // much as the camera math: iOS Safari ignores user-scalable=no, so an
  // unhandled pinch triggers PAGE zoom and a full-page re-raster. on a
  // memory-tight phone that raster spike is a tab kill.
  let touchPinchDist = 0;
  let touchPanX = 0;
  let touchPanY = 0;
  function onTouchStart(event) {
    if (event.touches.length !== 2) return;
    event.preventDefault();
    const [a, b] = event.touches;
    touchPinchDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    touchPanX = (a.clientX + b.clientX) / 2;
    touchPanY = (a.clientY + b.clientY) / 2;
  }
  function onTouchMove(event) {
    if (event.touches.length !== 2 || touchPinchDist <= 0) return;
    event.preventDefault();
    const [a, b] = event.touches;
    const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    if (dist > 0) {
      orbitDistanceGoal = Math.max(20, Math.min(160, orbitDistanceGoal * (touchPinchDist / dist)));
      touchPinchDist = dist;
    }
    const midX = (a.clientX + b.clientX) / 2;
    const midY = (a.clientY + b.clientY) / 2;
    const right = _right;
    const forward = _forward;
    camera.getWorldDirection(forward);
    right.crossVectors(forward, camera.up).normalize();
    forward.crossVectors(camera.up, right).normalize();
    const panScale = 0.15 * (orbitDistanceGoal / 160);
    orbitTargetGoal.addScaledVector(right, -(midX - touchPanX) * panScale);
    orbitTargetGoal.addScaledVector(forward, (midY - touchPanY) * panScale);
    orbitTargetGoal.y = 0;
    clampPanTarget();
    touchPanX = midX;
    touchPanY = midY;
  }
  function onTouchEnd(event) {
    if (event.touches.length < 2) touchPinchDist = 0;
  }

  canvas.addEventListener('wheel', onWheel, { passive: false });
  canvas.addEventListener('mousedown', onMouseDown);
  canvas.addEventListener('mousemove', onMouseMove);
  canvas.addEventListener('mouseup', onMouseUp);
  canvas.addEventListener('touchstart', onTouchStart, { passive: false });
  canvas.addEventListener('touchmove', onTouchMove, { passive: false });
  canvas.addEventListener('touchend', onTouchEnd);
  canvas.addEventListener('touchcancel', onTouchEnd);
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());

  let disposed = false;

  function disposeMaterial(m) {
    if (m.map) m.map.dispose();
    if (m.normalMap) m.normalMap.dispose();
    if (m.roughnessMap) m.roughnessMap.dispose();
    if (m.metalnessMap) m.metalnessMap.dispose();
    if (m.emissiveMap) m.emissiveMap.dispose();
    m.dispose();
  }

  function dispose() {
    disposed = true;
    unsubscribeGraphics();
    if (animationId) cancelAnimationFrame(animationId);
    animationId = null;
    if (pollId) cancelAnimationFrame(pollId);
    if (typeof window !== 'undefined' && window.__valkCaptureCanvas === canvas) {
      window.__valkCaptureCanvas = null;
      window.__valkCaptureRender = null;
    }
    canvas.removeEventListener('wheel', onWheel);
    canvas.removeEventListener('mousedown', onMouseDown);
    canvas.removeEventListener('mousemove', onMouseMove);
    canvas.removeEventListener('mouseup', onMouseUp);
    canvas.removeEventListener('touchstart', onTouchStart);
    canvas.removeEventListener('touchmove', onTouchMove);
    canvas.removeEventListener('touchend', onTouchEnd);
    canvas.removeEventListener('touchcancel', onTouchEnd);

    scene.traverse((obj) => {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach(disposeMaterial);
      }
    });

    tableTexture?.image?.close?.();
    tableTexture?.dispose();
    // uiOverlayMaterial.map is shared by the uiOverlayGenerator's
    // internal cache. Let the generator own the texture lifecycle.
    disposePlaymatUiTextures();
    uiOverlayMaterial.dispose();
    vanillaOverlayMaterial.map?.dispose();
    vanillaOverlayMaterial.dispose();
    vanillaBgTextureRef?.dispose();
    vanillaBgMaterialRef?.dispose();
    bgTextureRef?.dispose();
    cloudLayers.length = 0;
    for (const ff of fireflies) {
      ff.material.map?.dispose();
      ff.material.dispose();
    }
    fireflies.length = 0;
    lamps.length = 0;
    composer.dispose();
    renderer.dispose();
    renderer.forceContextLoss();
  }

  function setOrbitTheta(theta) {
    orbitTheta = theta;
    flipped = Math.abs(theta) > Math.PI / 2;
    updateCameraFromOrbit();
    syncArtRotation();
  }

  function pan(dx, dz) {
    orbitTargetGoal.x += dx;
    orbitTargetGoal.z += dz;
    clampPanTarget();
  }

  function setKeyHeld(key, held) {
    if (held) {
      heldKeys.add(key);
    } else {
      heldKeys.delete(key);
    }
  }

  function animateOrbitTo(targetX, targetZ, dist, phi, theta, duration = 500) {
    const orbitState = { x: orbitTarget.x, z: orbitTarget.z, dist: orbitDistance, phi: orbitPhi, theta: orbitTheta };

    addTween({ target: orbitState, property: 'x', from: orbitTarget.x, to: targetX, duration });
    addTween({ target: orbitState, property: 'z', from: orbitTarget.z, to: targetZ, duration });
    addTween({ target: orbitState, property: 'dist', from: orbitDistance, to: dist, duration });
    addTween({ target: orbitState, property: 'phi', from: orbitPhi, to: phi, duration });
    addTween({ target: orbitState, property: 'theta', from: orbitTheta, to: theta, duration });

    // Update orbit from the animated state each frame via a polling approach
    const startTime = performance.now();
    function poll() {
      orbitTarget.x = orbitState.x;
      orbitTarget.z = orbitState.z;
      orbitDistance = orbitState.dist;
      orbitPhi = orbitState.phi;
      orbitTheta = orbitState.theta;
      // Keep the glide goals pinned to the tween so the frame loop's
      // damping doesn't drag the camera back toward a stale goal.
      orbitTargetGoal.copy(orbitTarget);
      orbitDistanceGoal = orbitDistance;
      updateCameraFromOrbit();
      if (performance.now() - startTime < duration + 50) {
        pollId = requestAnimationFrame(poll);
      }
    }
    poll();
  }

  function zoomToOverview() {
    // Offset the target slightly toward the camera so the table
    // appears visually centred in the viewport. The near-top-down
    // angle (phi=0.1) makes the table look like it sits low
    // without this compensation.
    const zOffset = flipped ? -6 : 6;
    animateOrbitTo(0, zOffset, 160, 0.1, flipped ? Math.PI : 0, 600);
  }

  function zoomToCard(x, z) {
    animateOrbitTo(x, z, 30, 0.15, flipped ? Math.PI : 0, 400);
  }

  let flipped = false;

  // Playmat art is fixed on the table and never rotates with the
  // camera. When a player flips perspective (Shift+3), they see the
  // board from the opponent's side with the art facing the opponent.
  // This is intentional. It lets players appreciate how the board
  // looks to their opponent. The UI overlay also stays fixed (grid
  // numbering must match between players).
  function syncArtRotation() {
    // no-op: art stays in its original orientation regardless of camera
  }

  function flipPerspective() {
    flipped = !flipped;
    syncArtRotation();
    animateOrbitTo(0, 0, orbitDistance, orbitPhi, flipped ? Math.PI : 0, 600);
  }

  // Update the blurred background plane to match the current playmat art.
  function updateBackground(url) {
    if (!bgMaterialRef) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (disposed) return;
      const c = document.createElement('canvas');
      c.width = 512;
      c.height = 512;
      const ctx = c.getContext('2d');
      ctx.filter = 'blur(4px)';
      ctx.drawImage(img, 0, 0, 512, 512);
      if (bgTextureRef) bgTextureRef.dispose();
      bgTextureRef = new THREE.CanvasTexture(c);
      bgTextureRef.colorSpace = THREE.SRGBColorSpace;
      bgMaterialRef.map = bgTextureRef;
      bgMaterialRef.needsUpdate = true;
    };
    img.src = url;
  }

  // Load a texture entirely off the main thread. The WebP/PNG decode
  // happens via createImageBitmap so the UI never stutters.
  async function loadTextureAsync(url) {
    const res = await fetch(url);
    const blob = await res.blob();
    // Pre-flip vertically: WebGL's UNPACK_FLIP_Y_WEBGL is ignored
    // for ImageBitmap uploads, so we flip during decode instead.
    let bitmap = await createImageBitmap(blob, { imageOrientation: 'flipY' });
    // Playmat-scale sources (up to 10300px) carry more texels than the
    // drawing buffer can ever resolve on this display. Downscale at decode
    // so GPU + decoded memory track the display, not the asset.
    const budget = playmatTexelBudget();
    if (bitmap.width > budget) {
      const scaled = await createImageBitmap(bitmap, {
        resizeWidth: budget,
        resizeHeight: Math.round(bitmap.height * (budget / bitmap.width)),
        resizeQuality: 'high',
      });
      bitmap.close();
      bitmap = scaled;
    }
    const tex = new THREE.Texture(bitmap);
    tex.flipY = false;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    return tex;
  }

  // Set playmat texture(s). All image decoding happens off-thread.
  // The caller (PlaymatPicker) shows a loading overlay that hides
  // the instant swap, so no cross-fade is needed here.
  async function setPlaymat(urlOrLayers) {
    if (typeof urlOrLayers === 'string') {
      // Reset layer cache so the next layered switch reloads art
      setPlaymat._lastArt = null;
      setPlaymat._lastUi = null;
      const tex = await loadTextureAsync(urlOrLayers);
      if (topMaterial.map) topMaterial.map.dispose();
      topMaterial.map = tex;
      topMaterial.needsUpdate = true;
      // Don't dispose. Cached UI textures are owned by the generator.
      uiOverlayMaterial.map = null;
      uiOverlayMesh.visible = false;
      syncArtRotation();
      updateBackground(urlOrLayers);
      return;
    }

    const { art, uiConfig } = urlOrLayers;
    const artChanged = art !== setPlaymat._lastArt;
    setPlaymat._lastArt = art;
    setPlaymat._lastUi = JSON.stringify(uiConfig || {});

    // UI overlay is now procedurally generated from the per-theme +
    // per-player uiConfig (see uiOverlayGenerator). The generator
    // caches CanvasTextures internally, so flipping between settings
    // doesn't re-render the canvas. It just swaps the cached
    // texture onto the material.
    const [artTex, uiTex] = await Promise.all([
      artChanged ? loadTextureAsync(art) : Promise.resolve(null),
      uiConfig ? generatePlaymatUiTexture(uiConfig) : Promise.resolve(null),
    ]);

    if (artTex) {
      if (topMaterial.map) topMaterial.map.dispose();
      topMaterial.map = artTex;
      topMaterial.needsUpdate = true;
      updateBackground(art);
    }
    // Cached UI textures are SHARED across calls. Never .dispose()
    // them here, the generator's cache owns the lifecycle.
    uiOverlayMaterial.map = uiTex;
    uiOverlayMaterial.needsUpdate = true;
    uiOverlayMesh.visible = !!uiTex;
    syncArtRotation();
  }
  setPlaymat._lastArt = null;
  setPlaymat._lastUi = null;

  // v1.74. Preload + cache the vanilla Valkenhall playmat texture
  // for the subsurface crossfade. Both the user's playmat (managed
  // by setPlaymat above) and this vanilla layer stay loaded so the
  // setSubsurfaceState tween is GPU-only. No fetch, no decode, no
  // shader recompile.
  //
  // Also builds the matching blurred bg texture so the floor plane
  // behind the table can crossfade alongside the playmat surface.
  //
  // Accepts either a legacy string URL OR the new layered shape
  // `{art, uiConfig}` returned by getVanillaPlaymatUrl. Only the
  // art URL matters here. The procedural UI overlay is already on
  // top of both the surface and subsurface meshes, shared across
  // both states.
  async function setVanillaPlaymat(urlOrLayers) {
    const url = typeof urlOrLayers === 'string' ? urlOrLayers : urlOrLayers?.art;
    if (!url || url === setVanillaPlaymat._lastUrl) return;
    setVanillaPlaymat._lastUrl = url;

    // Surface vanilla texture for the table overlay.
    const tex = await loadTextureAsync(url);
    if (disposed) {
      tex.dispose();
      return;
    }
    vanillaOverlayMaterial.map?.dispose();
    vanillaOverlayMaterial.map = tex;
    vanillaOverlayMaterial.needsUpdate = true;

    // Blurred bg texture for the floor underlay. Mirrors the recipe
    // in updateBackground. 512×512 canvas, 4px blur, CanvasTexture.
    if (!vanillaBgMaterialRef) return;
    const bgImg = new Image();
    bgImg.crossOrigin = 'anonymous';
    bgImg.onload = () => {
      if (disposed) return;
      const blurCanvas = document.createElement('canvas');
      blurCanvas.width = 512;
      blurCanvas.height = 512;
      const ctx = blurCanvas.getContext('2d');
      ctx.filter = 'blur(4px)';
      ctx.drawImage(bgImg, 0, 0, 512, 512);
      vanillaBgTextureRef?.dispose();
      vanillaBgTextureRef = new THREE.CanvasTexture(blurCanvas);
      vanillaBgTextureRef.colorSpace = THREE.SRGBColorSpace;
      vanillaBgMaterialRef.map = vanillaBgTextureRef;
      vanillaBgMaterialRef.needsUpdate = true;
    };
    bgImg.src = url;
  }
  setVanillaPlaymat._lastUrl = null;

  // Drive the subsurface crossfade. `t` runs 0 (surface. User's
  // playmat fully visible) to 1 (subsurface. Vanilla fully covers
  // the custom layer). Cheap: a couple of material.opacity writes
  // per frame, no texture work.
  // Per-firefly takeoff delays are regenerated each time the transition
  // object changes (fresh stagger pattern on every toggle, matching
  // the card flight). The delay is stored on the firefly entry itself.
  // `_subsurfaceProgress` / `_subsurfaceTransition` are declared
  // earlier in the file (before onFrame) so the per-frame readers
  // never hit a TDZ error.

  function setSubsurfaceState({ progress = 0, transition = null } = {}) {
    const clamped = Math.max(0, Math.min(1, progress));

    // Detect a new transition (fresh startTime) and re-seed firefly
    // staggers so each toggle has its own takeoff pattern.
    if (transition?.startTime !== _subsurfaceTransition?.startTime) {
      const stagger = transition?.staggerWindow ?? 0;
      for (const ff of fireflies) {
        ff._subsurfaceDelay = stagger > 0 ? Math.random() * stagger : 0;
      }
    }
    _subsurfaceProgress = clamped;
    _subsurfaceTransition = transition;

    // ── Playmat crossfade (vanilla underlay ↔ user's playmat) ──
    // Skip while the vanilla texture is still streaming in.
    // The procedural UI overlay stays visible the whole time. It's
    // theme-agnostic, so the same grid sits on top of both the user's
    // art and the vanilla art and reads correctly in both states.
    if (!vanillaOverlayMaterial.map) return;
    vanillaOverlayMaterial.opacity = clamped;

    // ── Background crossfade (vanilla bg overlay ↔ user's bg) ──
    // Only writes if the vanilla bg blur has already been built.
    // otherwise the overlay stays at opacity 0 (no map = nothing
    // visible anyway).
    if (vanillaBgMaterialRef?.map) {
      vanillaBgMaterialRef.opacity = clamped;
    }
  }

  return {
    scene,
    camera,
    renderer,
    raycaster,
    tableMesh,
    resize,
    dispose,
    setPlaymat,
    setVanillaPlaymat,
    setSubsurfaceState,
    setUiInvertRegions,
    // Hot-rebuild the UI overlay texture with the current
    // playmat's config plus `skipComponents` flipped. The component
    // editor calls this on open (skipComponents=true → SVGs vanish
    // from the texture so the draggable 3D meshes are the only
    // visible copy) and on close (skipComponents=false → SVGs bake
    // back into the texture).
    async setUiSkipComponents(skip) {
      if (!setPlaymat._lastUi) return;
      try {
        const cfg = JSON.parse(setPlaymat._lastUi);
        const tex = await generatePlaymatUiTexture({ ...cfg, skipComponents: !!skip });
        uiOverlayMaterial.map = tex;
        uiOverlayMaterial.needsUpdate = true;
      } catch (_) {}
    },
    // Playmat-local UV ↔ world coords. The playmat is a flat
    // rounded-rectangle ShapeGeometry of TABLE_WIDTH × TABLE_HEIGHT
    // rotated -π/2 around X, with the UV remap u = x/W + 0.5,
    // v = y/H + 0.5 baked at geometry-build time. Exposing the
    // conversion here keeps the dev-mode UI-inversion editor honest:
    // pointer raycasts return world coords, the editor needs UV.
    tableWidth: TABLE_WIDTH,
    tableHeight: TABLE_HEIGHT,
    worldToUv(point) {
      return {
        u: point.x / TABLE_WIDTH + 0.5,
        v: 0.5 - point.z / TABLE_HEIGHT,
      };
    },
    uvToWorld({ u, v }) {
      return {
        x: (u - 0.5) * TABLE_WIDTH,
        z: (0.5 - v) * TABLE_HEIGHT,
      };
    },
    updateMouse,
    raycastObjects,
    raycastTablePoint,
    pan,
    setOrbitTheta,
    setKeyHeld,
    zoomToOverview,
    zoomToCard,
    flipPerspective,
    onFrame,
    CARD_REST_Y: 0.05 + CARD_THICKNESS / 2,
    CARD_DRAG_Y: 0.05 + CARD_THICKNESS / 2 + 2,
    setViewInsetLeft(frac) {
      viewInsetLeftFrac = frac || 0;
      applyViewInset();
      camera.updateProjectionMatrix();
    },
    setLightingMode(mode) {
      lightingMode = mode;
      // Apply immediately instead of waiting for the next 120-frame tick
      todUpdateTimer = 119;
    },
    getLightingMode() { return lightingMode; },
    setPaused(paused) { renderPaused = paused; },
  };
}
